/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    ConfigurationParameters,
    Configuration,
    AiringOffer,
    WatchLiveTVApi,
    GetAiringOffersRequest,
} from 'client-code/airing';
import { IAiringOfferServiceRepository } from '../interfaces/IAiringOfferServiceRepository';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types/TServicesParams';
import { Constants } from '../constants';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { CommonHeaders } from '../CommonHeaders';
import { AppUtils } from '../AppUtils';
import { DeviceModel } from '../device/DeviceModel';
import { TAiringOffer } from '../types/TAiringOffer';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class AiringOfferServiceRepositoryImpl implements IAiringOfferServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'AiringOfferServiceRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getAiringOfferData(stationId: string, time: string): Promise<Array<AiringOffer>> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;
            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: GetAiringOffersRequest = {
                stationId,
                time,
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: DeviceModel.getAppVersionName(),
                bodyId: this.mParams.bodyId,
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: DeviceModel.getTiVoSettings().getAppName(),
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
            };

            const configuration = new Configuration(configurationParameters);
            const airingService = new WatchLiveTVApi(configuration);
            const airingResponse = await airingService.getAiringOffers(requestParameters);

            return airingResponse.data;
        } catch (error) {
            Log.e(this.TAG, 'getAiringOfferData()', error);
            throw error;
        }
    }

    /**
     * Checking for the currentAiringOffer and also returning the next AiringOffer
     */
    private async returnCurrentAndNextAiringOffer(
        stationId: string,
        networkCallId?: number,
        playerCurrentTime?: number,
    ): Promise<TAiringOffer> {
        const currentTime = playerCurrentTime || new Date().getTime();
        /**
         * time here returned is in ISO time format
         * for example: 2022-09-05T19:00:00Z, without milliseconds
         */
        const timeRoundedDownToFifteenMinutes =
            Math.floor(currentTime / Constants.MILLISECONDS_IN_FIFTEEN_MINUTES) *
            Constants.MILLISECONDS_IN_FIFTEEN_MINUTES;
        const time = AppUtils.timeInISOFormatWithoutMilliSeconds(timeRoundedDownToFifteenMinutes);
        const airingOffersData: Array<AiringOffer> = await this.getAiringOfferData(stationId, time);
        const airingOffers: Array<AiringOffer> = [];
        for (let i = 0; i < airingOffersData.length - 1; i += 1) {
            const currentAiringOffer = airingOffersData[i];
            const currentAiringOfferStartTime: number = new Date(
                currentAiringOffer.startTime,
            ).getTime();
            const currentAiringOfferEndTime: number =
                currentAiringOfferStartTime +
                currentAiringOffer.duration * Constants.MILLISECOND_IN_ONE_SECOND;

            if (
                currentTime >= currentAiringOfferStartTime &&
                currentTime < currentAiringOfferEndTime
            ) {
                airingOffers.push(currentAiringOffer);
                airingOffers.push(airingOffersData[i + 1]);
                break;
            }
        }

        Log.i(this.TAG, 'returning current and next Airing Offers');
        return <TAiringOffer>{ airingOffers, networkCallId };
    }

    /**
     * This function will return an array of:
     * empty Array if no airing Offers
     * One Airing Offer if only current Airing Offer is available
     * Two Airing Offers if current and next Airing Offers are available
     * @param stationId StationId
     */
    async fetchAiringOffers(
        stationId: string,
        networkCallId?: number,
        playerCurrentTime?: number,
    ): Promise<TAiringOffer> {
        try {
            return await this.returnCurrentAndNextAiringOffer(
                stationId,
                networkCallId,
                playerCurrentTime,
            );
        } catch (error) {
            Log.e(this.TAG, 'fetchAiringOffers()', error);
            throw error;
        }
    }
}
